/**
 * @architecture-header 1
 * @layer host
 * @domain browser
 * @responsibility Mount @vispo/workspace-web with BrowserRouter history and browser adapters.
 * @state-owner browser history
 * @allowed-imports React DOM; React Router; browser host; @vispo/workspace-web public API/styles
 * @forbidden domain modules; duplicated router tree; Electron source
 * @public-api Vite application entry
 * @side-effects mounts React into #root
 * @tests Playwright deep-link and core-flow tests
 */

import { createWorkspaceRoutes, WorkspaceRoot } from '@vispo/workspace-web';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter } from 'react-router-dom';
import '@vispo/workspace-web/styles.css';
import { createBrowserEnvironment } from './browser-host';
import { resetBrowserStateForLocalRun } from './browser-storage';
import { loadBrowserRuntimeConfig } from './runtime-config';

async function bootstrap(): Promise<void> {
  const element = document.getElementById('root');
  if (!element) throw new Error('Missing #root mount element');
  const runtimeConfig = await loadBrowserRuntimeConfig();
  resetBrowserStateForLocalRun(runtimeConfig.localRunId);
  const browserEnvironment = createBrowserEnvironment(runtimeConfig);
  const router = createBrowserRouter(createWorkspaceRoutes());

  createRoot(element).render(
    <StrictMode>
      <WorkspaceRoot environment={browserEnvironment} router={router} />
    </StrictMode>,
  );
}

void bootstrap().catch(() => {
  const element = document.getElementById('root');
  if (element) element.textContent = '環境設定載入失敗，請稍後再試。';
});
